import { useEffect, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;

function authHeaders() {
  return { "Content-Type": "application/json", token: localStorage.getItem("token") };
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <label className="theme-panel-muted flex cursor-pointer items-center justify-between gap-4">
      <span>
        <span className="block text-sm font-semibold text-slate-800">{label}</span>
        <span className="mt-1 block text-xs text-slate-600">{description}</span>
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-5 w-5 accent-sky-600" />
    </label>
  );
}

function UserSettings({ darkMode, setDarkMode }) {
  const [profile, setProfile] = useState(null);
  const [email, setEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function loadProfile() {
    try {
      const response = await axios.get(`${API_URL}/users/me`, { headers: authHeaders() });
      setProfile(response.data.user);
      setEmail(response.data.user.email || "");
      setError("");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Kunde inte hämta kontoinställningar");
    }
  }

  useEffect(() => { loadProfile(); }, []);

  async function saveEmail(event) {
    event.preventDefault();
    try {
      const response = await axios.patch(`${API_URL}/users/me/email`, { email, currentPassword: emailPassword }, { headers: authHeaders() });
      setMessage(response.data.message);
      setError("");
      setEmailPassword("");
      localStorage.removeItem("token");
      window.location.assign("/resend-verification");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "E-postadressen kunde inte sparas");
      setMessage("");
    }
  }

  async function resendVerification() {
    try {
      const response = await axios.post(`${API_URL}/users/me/resend-verification`, {}, { headers: authHeaders() });
      setMessage(response.data.message);
      setError("");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Verifieringsmejlet kunde inte köas");
    }
  }

  async function updatePreference(key, value) {
    const previous = profile.notificationPreferences || {};
    setProfile({ ...profile, notificationPreferences: { ...previous, [key]: value } });
    try {
      const response = await axios.patch(`${API_URL}/users/me/notification-preferences`, { [key]: value }, { headers: authHeaders() });
      setProfile((current) => ({ ...current, notificationPreferences: response.data.notificationPreferences }));
      setError("");
    } catch (requestError) {
      setProfile((current) => ({ ...current, notificationPreferences: previous }));
      setError(requestError.response?.data?.message || "Notifieringsinställningen kunde inte sparas");
    }
  }

  async function changePassword(event) {
    event.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setError("De nya lösenorden matchar inte");
      return;
    }
    try {
      const response = await axios.patch(`${API_URL}/users/change-password`, { currentPassword: password, newPassword }, { headers: authHeaders() });
      setMessage(response.data.message);
      setError("");
      setPassword(""); setNewPassword(""); setConfirmNewPassword("");
    } catch (requestError) {
      setError(requestError.response?.data?.message || "Lösenordet kunde inte ändras");
      setMessage("");
    }
  }

  const preferences = profile?.notificationPreferences || {
    emailGroupInvitations: true,
    emailSharedSessions: true,
    inAppGroupInvitations: true,
    inAppSharedSessions: true
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-4 py-4">
      {message && <div className="theme-alert-success">{message}</div>}
      {error && <div className="theme-alert-error">{error}</div>}

      <section className="theme-card">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Konto</p>
        <h1 className="mt-2 text-lg font-bold text-slate-800">E-postadress</h1>
        <p className="mt-2 text-sm text-slate-600">Namn: <strong>{profile?.name || "–"}</strong></p>
        <form onSubmit={saveEmail} className="mt-4 grid gap-3 sm:grid-cols-2">
          <input className="theme-input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="E-postadress" required />
          <input className="theme-input" type="password" value={emailPassword} onChange={(event) => setEmailPassword(event.target.value)} placeholder="Nuvarande lösenord" required />
          <button className="theme-button-primary sm:col-span-2 sm:w-64" type="submit">Spara och verifiera</button>
        </form>
        {profile?.email && (
          <div className={`mt-4 ${profile.emailVerifiedAt ? "theme-alert-success" : "theme-alert-error"}`}>
            {profile.emailVerifiedAt ? "E-postadressen är verifierad." : "E-postadressen är inte verifierad."}
            {!profile.emailVerifiedAt && <button className="ml-3 underline" type="button" onClick={resendVerification}>Skicka länken igen</button>}
          </div>
        )}
      </section>

      <section className="theme-card">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Notifieringar</p>
        <h2 className="mt-2 text-lg font-bold text-slate-800">Välj hur du vill bli meddelad</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {profile?.role === 1 && <Toggle checked={preferences.emailGroupCreated !== false} onChange={(value) => updatePreference("emailGroupCreated", value)} label="Nya föreningsgrupper via e-post" description="Få ett mejl när en ny föreningsgrupp skapas. Kräver verifierad e-postadress." />}
          {profile?.role === 1 && <Toggle checked={preferences.emailPendingApprovals !== false} onChange={(value) => updatePreference("emailPendingApprovals", value)} label="Kontogodkännande via e-post" description="Få ett mejl när en användare har verifierat sin e-postadress och väntar på godkännande." />}
          <Toggle checked={preferences.inAppGroupInvitations !== false} onChange={(value) => updatePreference("inAppGroupInvitations", value)} label="Gruppinbjudningar i appen" description="Visas i notiscentret." />
          <Toggle checked={preferences.emailGroupInvitations !== false} onChange={(value) => updatePreference("emailGroupInvitations", value)} label="Gruppinbjudningar via e-post" description="Kräver verifierad e-postadress." />
          <Toggle checked={preferences.inAppSharedSessions !== false} onChange={(value) => updatePreference("inAppSharedSessions", value)} label="Delade pass i appen" description="Meddela när ett pass delas med din grupp." />
          <Toggle checked={preferences.emailSharedSessions !== false} onChange={(value) => updatePreference("emailSharedSessions", value)} label="Delade pass via e-post" description="Kräver verifierad e-postadress." />
        </div>
      </section>

      <section className="theme-card">
        <h2 className="text-lg font-bold text-slate-800">Utseende</h2>
        <Toggle checked={darkMode} onChange={setDarkMode} label="Mörkt läge" description="Använd ett mörkt tema i hela appen." />
      </section>

      <section className="theme-card">
        <form onSubmit={changePassword} className="grid gap-3">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Säkerhet</p>
          <h2 className="text-lg font-bold text-slate-800">Ändra lösenord</h2>
          <input className="theme-input" value={password} onChange={(event) => setPassword(event.target.value)} type="password" placeholder="Nuvarande lösenord" required />
          <input className="theme-input" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} type="password" placeholder="Nytt lösenord" minLength="8" required />
          <input className="theme-input" value={confirmNewPassword} onChange={(event) => setConfirmNewPassword(event.target.value)} type="password" placeholder="Bekräfta nytt lösenord" minLength="8" required />
          <button className="theme-button-primary sm:w-64" type="submit">Ändra lösenord</button>
        </form>
      </section>
    </div>
  );
}

export default UserSettings;
